/* eslint-disable @typescript-eslint/no-explicit-any */
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';

import { Observable, tap } from 'rxjs';

import { Option } from '@hmax-order/shared/types';
import { CapitalizePipe } from '@hmax-order/frontend/utils';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ValidateFieldsService } from '../validate-fields.service';

@Component({
  selector: 'hmax-input-select',
  standalone: true,
  templateUrl: './input-select.component.html',
  styleUrls: ['./input-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
    TranslocoModule,
    CapitalizePipe,
  ],
})
export class InputSelectComponent implements OnInit, OnChanges {
  public validateField = inject(ValidateFieldsService);
  private _transloco = inject(TranslocoService);

  @Input() label!: string;
  @Input() translateLabel = true;
  @Input() emptyOptionText!: string;
  @Input() showEmptyOption = true;
  @Input() options!: Option[] | Observable<Option[]>;
  @Input() optionsType: 'object' | 'observable' = 'object';
  @Input() optionTranslate = false;
  @Input() formGroup!: FormGroup;
  @Input() controlName!: string;
  @Input() multiple = false;
  @Input() appearance: 'outline' | 'fill' = 'outline';
  @Input() capitalize = true;
  /** Opção "Todos" no topo da lista de um multi-select, com checkbox que marca/desmarca tudo de uma vez. */
  @Input() showSelectAll = false;
  @Input() selectAllLabel = 'all';
  /** `mat-select-trigger` compacto: mostra o label de "Todos" quando tudo está marcado, senão o resumo truncado. */
  @Input() compactMultipleTrigger = false;
  /** No resumo do multi-select, lista os rótulos inline ("a, b, c") em vez de "a +N". */
  @Input() multipleSummaryInline = false;

  @Output() selectedOption: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Ramo `observable`: o stream que o template consome pelo `async`, com um `tap` que espelha a última
   * lista emitida. É a ÚNICA assinatura do observable do consumidor — um `subscribe` próprio do
   * componente, além do `async`, refazia o `GET` de quem manda um observable HTTP (o seletor de
   * integrador manda `findAll()` cru). Rebuilt em `ngOnChanges`, porque `options` novo é stream novo.
   */
  options$: Observable<Option[]> | null = null;

  /** Última emissão do ramo `observable`; o ramo `object` lê o input direto (ver `_currentOptions`). */
  private _observableOptions: Option[] = [];

  /** `options` já resolvido em array, nos dois ramos, para "Todos" e para o resumo do trigger. */
  private get _currentOptions(): Option[] {
    return this.optionsType === 'observable' ? this._observableOptions : this.optionsAsArray;
  }

  get formControl(): AbstractControl {
    return this.formGroup.controls[this.controlName];
  }

  /**
   * `options` aceita as duas formas, e quem decide qual delas chegou é o `optionsType` — um input
   * separado, que o compilador não tem como usar para estreitar o tipo. Cada ramo do template lê pelo
   * getter da forma que ele próprio já garantiu pelo `@if`.
   */
  get optionsAsObservable(): Observable<Option[]> {
    return this.options as Observable<Option[]>;
  }

  get optionsAsArray(): Option[] {
    return (this.options ?? []) as Option[];
  }

  private get allOptionValues(): any[] {
    return this._currentOptions.map((option) => option.value);
  }

  /**
   * Estado do "Todos" DERIVADO do valor, nunca guardado num campo: assim o checkbox e o valor do
   * controle não têm como divergir.
   */
  get isAllSelected(): boolean {
    const values = this.formControl?.value;

    return this._currentOptions.length > 0 && Array.isArray(values) && values.length >= this._currentOptions.length;
  }

  get selectedMultipleSummary(): string {
    const values = this.formControl?.value;
    if (!Array.isArray(values) || !values.length) {
      return '';
    }

    // Inline: todos os rótulos na ORDEM das opções, truncado por CSS no trigger.
    if (this.multipleSummaryInline) {
      const ordered = this.allOptionValues.filter((opt) => values.some((sel) => this.selectedCompare(opt, sel)));
      const source = ordered.length ? ordered : values;
      return source
        .map((value) => this._getOptionName(value))
        .filter(Boolean)
        .join(', ');
    }

    const labels = values.map((value) => this._getOptionName(value)).filter(Boolean);
    if (!labels.length) {
      return '';
    }

    if (labels.length === 1) {
      return labels[0];
    }

    return `${labels[0]} +${labels.length - 1}`;
  }

  /**
   * Marca ou desmarca tudo de uma vez.
   *
   * O "Todos" fica FORA da lista de `mat-option` de propósito. Como opção com valor sentinela ele
   * renderizava o checkbox do próprio `mat-option` em cima do nosso (dois quadradinhos na mesma
   * linha) e voltava para o valor do controle a cada clique, e era esse vai e volta que fazia o
   * "Todos" marcar e nunca desmarcar.
   */
  toggleSelectAll(): void {
    const control = this.formControl;

    if (!control) {
      return;
    }

    control.setValue(this.isAllSelected ? [] : this.allOptionValues);
    control.markAsDirty();
    control.markAsTouched();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options'] || changes['optionsType']) {
      this._bindObservableOptions();
    }
  }

  ngOnInit(): void {
    // Carregar controlName com o label se não houver controlName definido
    this.controlName = this.controlName ?? this.label;
    // Input gravado sem binding (spec, `component.options = …`) não passa pelo `ngOnChanges`.
    this._bindObservableOptions();
  }

  private _bindObservableOptions(): void {
    this._observableOptions = [];
    this.options$ =
      this.optionsType === 'observable' && this.optionsAsObservable
        ? this.optionsAsObservable.pipe(tap((options) => (this._observableOptions = options ?? [])))
        : null;
  }

  /**
   * Método de comparação do select da acomodação.
   * Nele definimos que campos únicos devem ser comparados para determinar qual acomodação selecionar
   * @param selected Valor selecionado no select
   * @param option Um option do select passado como parâmetro para verificar se é o selecionado
   */
  selectedCompare(
    option: { value?: string | number | { id: string | number } } | any,
    selected: { value?: string | number | { id: string | number } } | any,
  ): boolean {
    if (!option || !selected) {
      return false;
    }

    const normalize = (obj: any) => obj?.value?.id ?? obj?.id ?? obj?.value ?? obj;

    return normalize(option) === normalize(selected);
  }

  private _getOptionName(value: any): string {
    const option = this._currentOptions.find((item) => this.selectedCompare(item?.value, value));
    const name = option?.name ?? String(value ?? '');
    // Respeita optionTranslate: sem isto o trigger compacto mostrava a chave crua em vez do rótulo traduzido.
    return this.optionTranslate && name ? this._transloco.translate(name) : name;
  }

  /**Emitir a opção que foi clicada */
  selectedOptionEmitter(value: any): void {
    this.selectedOption.emit(value);
  }
}
